import { DocumentArrowUpIcon, IdentificationIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { TargetBox } from 'components/DragDrop'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType, INVALID_ALL_INPUTS, SettingKey } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useRef, useState } from 'react'
import { NativeTypes } from 'react-dnd-html5-backend'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { accountTypeTitleForName, isManagedAccountParty } from 'reducers/partyNames'
import {
  type PartyAiExtractData,
  extractPartyFromPdf,
  getAdminConfig,
  getPartyAdditionalInfo,
  getRolodexCategories,
  getVendorOptionsPublic,
  submitRolodex,
  updateParties,
  uploadFiles,
} from 'services'
import { Button, Modal } from 'stories/components'
import { InputConvert, InputValidate, openAuditLog } from 'utils'
import { confirmOptions } from 'utils/modals/confirmOptions'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs, defaultValuesIfEmpty, extraFieldGroups, extraInputsOnGroup, inputGroups } from './config'
import { RolodexModal } from './RolodexModal'

const isAcceptedPartyDoc = (name: string) => {
  const lower = (name || '').toLowerCase()
  return lower.endsWith('.pdf') || /\.(png|jpe?g|webp|gif|tiff?)$/i.test(lower)
}

export const PartyModal = ({
  item,
  isConfig = false,
  ...props
}: {
  rolodexShow?: boolean
  isConfig?: boolean
  onClose: any
  item: Record<string, any> | null
  poolNumber?: number
}) => {
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs())
  const [groups, setGroups] = useState(inputGroups)
  const [isShowOverlay, setShowOverlay] = useState(false)
  const [isShowRolodexModal, setShowRolodexModal] = useState(false)
  const [obiePartnerId, setObiePartnerId] = useState('')
  const [categories, setCategories] = useState<Record<string, string>>({})
  const [partyFieldConfig, setPartyFieldConfig] = useState<Record<string, any>>({})
  const [partyFieldConfigLoaded, setPartyFieldConfigLoaded] = useState(false)
  const [partyAiEnabled, setPartyAiEnabled] = useState(false)
  const [inputsHydrated, setInputsHydrated] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const contractorAutoFillDoneRef = useRef(false)
  const userEditedRef = useRef(false)

  const isPool = !!props.poolNumber
  const { hasPermission } = usePermissions()
  const canAiExtract = !isConfig && !!item && hasPermission('Loan.Party.AIExtract') && partyAiEnabled

  const profile = useSelector((state: any) => {
    return state.auth.profile
  })

  useEffect(() => {
    getVendorOptionsPublic('obie').then(({ enabled, partnerId }: { enabled: boolean; partnerId: string }) => {
      enabled && setObiePartnerId(partnerId)
    })
    getRolodexCategories().then((data) => setCategories(data))
    getAdminConfig('partyFieldConfig').then((res) => {
      setPartyFieldConfig(res)
      setPartyFieldConfigLoaded(true)
    })
  }, [])

  useEffect(() => {
    if (!hasPermission('Loan.Party.AIExtract')) {
      setPartyAiEnabled(false)
      return
    }
    getAdminConfig(SettingKey.GLOBAL_SETTINGS).then((settings: any) => {
      setPartyAiEnabled(!!settings?.partyAiAutofillEnabled)
    })
  }, [])

  useEffect(() => {
    contractorAutoFillDoneRef.current = false
    userEditedRef.current = false
    setInputsHydrated(false)
  }, [item])

  const applyAdditionalInfo = (data: Record<string, InputType>, resData: Record<string, any>) => {
    const tempInput = cloneDeep(data)
    Object.keys(resData || {}).forEach((key) => {
      if (!tempInput[key]) return
      // Keep an existing Title; only fill when blank
      if (key === 'Title' && tempInput[key].value) return
      tempInput[key].value = resData[key]
      tempInput[key].error = InputValidate(tempInput[key])
    })
    return tempInput
  }

  const fillRelatedPartyInfo = async (data: any, type: string) => {
    setLoading(true)
    try {
      const res = await getPartyAdditionalInfo(type)
      let resData = res
      if (type === 'Contractor-contractor') {
        if (!Array.isArray(res) || !res.length) return
        if (res.length === 1) {
          resData = res[0]
        } else {
          const options: Array<any> = res.map((contractor: any, index: number) => ({
            name: contractor.Company,
            desc: `${contractor.FirstName || ''} ${contractor.LastName || ''}`.trim(),
            value: index,
          }))
          const optionRes: any = await confirmOptions('Choose Contractor...', options)
          if (optionRes === false) return
          resData = res[optionRes]
        }
      }
      if (!resData || typeof resData !== 'object') return
      switch (type) {
        case 'Contractor-borrower':
        case 'Contractor-contractor':
        case 'Appraiser':
        case 'Valuation Reviewer': {
          setInputs(convertAdditionalInfo(applyAdditionalInfo(data, resData)))
          break
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const fillHarzardInsuranceCompanyInfo = async (data: Record<string, InputType>) => {
    let tempInput = cloneDeep(data)

    Object.keys(tempInput).map((key) => {
      if (tempInput[key].required) tempInput[key].value = 'TBD'
      tempInput[key].error = ''
    })
    tempInput['State'].value = 'AL'
    tempInput['Zip'].value = '11111'
    tempInput['WorkPhone'].value = '111-111-1111'
    tempInput['EMail'].value = 'tbd@tbd.com'

    setInputs(convertAdditionalInfo(tempInput))
  }

  const convertAdditionalInfo = (data: any) => {
    if (!item) return data
    switch (item.Type) {
      case 'Appraiser':
        data['Company'].additionalElements = (
          <span
            className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
            onClick={() => fillRelatedPartyInfo(data, 'Appraiser')}
          >
            Fill Valuation Review Info
          </span>
        )
        break
      case 'Valuation Reviewer':
        data['Company'].additionalElements = (
          <span
            className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
            onClick={() => fillRelatedPartyInfo(data, 'Valuation Reviewer')}
          >
            Fill from Loan Overview
          </span>
        )
        break
      case 'Contractor':
        data['Company'].additionalElements = (
          <>
            <span
              className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
              onClick={() => fillRelatedPartyInfo(data, 'Contractor-borrower')}
            >
              Borrower Info
            </span>
            <span
              className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
              onClick={() => fillRelatedPartyInfo(data, 'Contractor-contractor')}
            >
              Contractor Info
            </span>
          </>
        )
        break
      case 'Hazard Insurance Company':
        data['Company'].additionalElements = () => (
          <span
            className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
            onClick={() => fillHarzardInsuranceCompanyInfo(data)}
          >
            Fill TBD
          </span>
        )
        break
    }
    return data
  }

  useEffect(() => {
    if (!item || !partyFieldConfigLoaded) return
    let newInputs = cloneDeep(defaultInputs())
    let newGroups = cloneDeep(inputGroups)
    for (var key in newInputs) {
      newInputs[key].value = item[key]
      // The Parties template only stores a display name and optional defaults. Required
      // flags are for a party on a loan, and must not block saving a custom name.
      newInputs[key].required = isConfig ? false : !!partyFieldConfig[key]?.required
    }
    if (defaultValuesIfEmpty[item.Type]) {
      Object.keys(defaultValuesIfEmpty[item.Type]).map((key) => {
        if (!item[key]) newInputs[key].value = defaultValuesIfEmpty[item.Type][key]
      })
    }

    if (extraFieldGroups[item.Type] !== undefined) {
      newGroups = {
        ...extraFieldGroups[item.Type],
        ...newGroups,
      }
    }
    if (extraInputsOnGroup[item.Type] !== undefined) {
      Object.keys(extraInputsOnGroup[item.Type]).map((key: string) => {
        Object.keys(extraInputsOnGroup[item.Type][key]).map((field) => {
          newGroups[key].splice(extraInputsOnGroup[item.Type][key][field], 0, field)
        })
      })
    }
    newInputs = convertAdditionalInfo(newInputs)
    setInputs(newInputs)
    setGroups(newGroups)
    setInputsHydrated(true)
  }, [item, partyFieldConfig, partyFieldConfigLoaded, isConfig])

  // Auto-fill empty Contractor party from Contractor Application once after hydrate
  useEffect(() => {
    if (!item || isConfig || !inputsHydrated || !partyFieldConfigLoaded) return
    if (item.Type !== 'Contractor') return
    if (contractorAutoFillDoneRef.current || userEditedRef.current) return
    const companyEmpty = !inputs.Company?.value
    const entityTypeEmpty = !inputs.EntityType?.value
    if (!companyEmpty || !entityTypeEmpty) return
    contractorAutoFillDoneRef.current = true
    fillRelatedPartyInfo(inputs, 'Contractor-contractor')
  }, [item, isConfig, inputsHydrated, partyFieldConfigLoaded, inputs.Company?.value, inputs.EntityType?.value])

  const onChange = (key: string, value: string) => {
    userEditedRef.current = true
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(convertAdditionalInfo(newInputs))
  }

  const visibleKeys = useMemo(() => {
    let rlt: any = []
    Object.keys(groups).map((key) => {
      rlt = [...rlt, ...groups[key]]
    })
    return rlt
  }, [groups])

  if (!item) return null

  const showHistory = async (key: string) => {
    const loanNumber = isPool ? props.poolNumber : setLoanNumber()

    const options = {
      table: 'Party',
      field: key,
      keys: {
        partyName: item?.Type,
        loanNumber: loanNumber,
        field: key,
      },
    }
    openAuditLog(options)
  }

  const onSubmit = async () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = cloneDeep(item)
    for (const key in inputs) {
      if (!isConfig && key === 'borrowerRequired') continue
      if (visibleKeys.includes(key)) {
        newInputs[key].error = InputValidate(newInputs[key])
        data[key] = newInputs[key].value
        if (newInputs[key].error) hasError = true
      }
    }
    setInputs(convertAdditionalInfo(newInputs))
    if (hasError) {
      return toast(INVALID_ALL_INPUTS, { type: 'error' })
    }
    setLoading(true)
    const loanNumber = setLoanNumber()
    updateParties(props.poolNumber ? props.poolNumber : Number(loanNumber), item.PartyID || 0, data, isPool)
      .then(() => {
        toast('Party is updated', { type: 'info' })
        data.Name = [data.FirstName, data.MiddleName, data.LastName].join(' ').trim()
        props.onClose(true, data)
      })
      .catch(() => setLoading(false))
  }

  const onClose = () => {
    props.onClose()
  }

  const onClickIdentification = () => {
    setShowOverlay(true)
  }

  const onSelectFromRolodex = () => {
    setShowOverlay(false)
    setShowRolodexModal(true)
  }

  const onAddToRolodex = () => {
    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = item
    for (const key in inputs) {
      data[key] = newInputs[key].value
    }
    setInputs(convertAdditionalInfo(newInputs))
    setLoading(true)
    data.Type = item.Type
    submitRolodex(0, data)
      .then(() => {
        toast('New Rolodex is added.', { type: 'success' })
        setShowOverlay(false)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  const onCloseRolodexModal = (data: Record<string, any>) => {
    setShowOverlay(false)
    setShowRolodexModal(false)
    if (!data) return
    let newInputs = cloneDeep(inputs)
    for (var key in newInputs) {
      if (data[key] !== undefined) newInputs[key].value = data[key]
    }
    setInputs(convertAdditionalInfo(newInputs))
  }

  const onObieClick = async () => {
    ;(window as any).Obie.open({
      partnerId: obiePartnerId,
      // sandbox: true,
    })
  }

  const applyAiExtract = (data: PartyAiExtractData) => {
    const newInputs = cloneDeep(inputs)
    let applied = 0
    Object.keys(data).forEach((key) => {
      const value = data[key]
      if (value == null || value === '' || !newInputs[key]) return
      const converted = InputConvert(newInputs[key], value)
      newInputs[key].value = converted
      newInputs[key].error = InputValidate({ ...newInputs[key], value: converted })
      applied += 1
    })
    if (!applied) {
      toast('No party fields could be extracted from the document.', { type: 'warning' })
      return
    }
    setInputs(convertAdditionalInfo(newInputs))
    toast(`Applied ${applied} field${applied === 1 ? '' : 's'} from the document. Review and click Update.`, {
      type: 'info',
    })
  }

  const runExtractFromFileKey = async (fileKey: string, name: string) => {
    setLoading(true)
    try {
      const res = await extractPartyFromPdf(item.Type || '', [{ fileKey: String(fileKey), name }])
      if (res?.success && res.data) {
        applyAiExtract(res.data)
      } else if (res?.success) {
        toast('Could not extract party fields from this document.', { type: 'warning' })
      } else if (!res?.message) {
        // Api already toasts when response includes { success: false, message }
        toast('Could not extract party fields from this document.', { type: 'warning' })
      }
    } catch {
      toast('Could not extract party fields from this document.', { type: 'warning' })
    } finally {
      setLoading(false)
    }
  }

  const runExtract = async (file: File) => {
    if (!isAcceptedPartyDoc(file.name)) {
      toast('Please use a PDF or image document.', { type: 'error' })
      return
    }
    const loanNumber = setLoanNumber()
    setLoading(true)
    try {
      const keys: string[] = await uploadFiles({ path: `loan/${loanNumber}/party-ai` }, [file])
      const fileKey = keys?.[0]
      if (!fileKey) {
        toast('Failed to upload document.', { type: 'error' })
        return
      }
      const res = await extractPartyFromPdf(item.Type || '', [{ fileKey: String(fileKey), name: file.name }])
      if (res?.success && res.data) {
        applyAiExtract(res.data)
      } else if (res?.success) {
        toast('Could not extract party fields from this document.', { type: 'warning' })
      } else if (!res?.message) {
        // Api already toasts when response includes { success: false, message }
        toast('Could not extract party fields from this document.', { type: 'warning' })
      }
    } catch {
      toast('Could not extract party fields from this document.', { type: 'warning' })
    } finally {
      setLoading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const onFilePicked = (files: FileList | File[] | null) => {
    const file = files?.[0]
    if (!file) return
    runExtract(file)
  }

  const onDropItem = (dropItem: any) => {
    if (dropItem?.files?.length) {
      onFilePicked(dropItem.files)
      return
    }
    const docs: any[] = Array.isArray(dropItem?.documents) ? dropItem.documents : [dropItem]
    const doc = docs.find((d) => d?.fileKey || d?.key) || docs[0]
    const fileKey = doc?.fileKey || doc?.key
    const name = doc?.name || 'document.pdf'
    if (!fileKey) {
      toast('Could not read the dropped document.', { type: 'error' })
      return
    }
    if (!isAcceptedPartyDoc(name)) {
      toast('Please drop a PDF or image document.', { type: 'error' })
      return
    }
    runExtractFromFileKey(String(fileKey), name)
  }

  return (
    <>
      <Modal
        title={
          <span className="flex items-center flex-wrap gap-2">
            <span className="flex items-center">
              Update {accountTypeTitleForName(item.Type)}
              {props.rolodexShow !== false && (
                <button type="button" onClick={onClickIdentification}>
                  <IdentificationIcon className="w-7 h-7 ml-2" />
                </button>
              )}
            </span>
            {canAiExtract && (
              <TargetBox
                types={['document', NativeTypes.FILE]}
                onDrop={onDropItem}
                className="ml-2 border border-dashed border-gray-300 hover:border-shade-blue rounded px-2 py-1 text-xs font-normal text-gray-600 cursor-pointer transition-colors"
                highlightClassName="border-shade-blue bg-blue-50"
              >
                <div
                  className="flex items-center gap-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    fileInputRef.current?.click()
                  }}
                >
                  <DocumentArrowUpIcon className="w-4 h-4 text-shade-blue" />
                  <span>Drop PDF/image to autofill</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => onFilePicked(e.target.files)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </TargetBox>
            )}
          </span>
        }
        titleOkay={'Update'}
        loading={loading}
        onClose={onClose}
        onOk={onSubmit}
        isOpen
      >
        <div className="text-gray-700 w-[calc(70vw)] text-left">
          {Object.keys(groups).map((title, index: number) => {
            if (title === 'Party Custom Info' && (!isConfig || isManagedAccountParty(item.Type))) return
            const inputGroup = (groups as any)[title]
            return (
              <div className="mb-5" key={title}>
                <p className="border-b font-bold text-sm w-full mb-2">
                  {title}{' '}
                  {profile.isBorrower && item.Type === 'Hazard Insurance Company' && !index && (
                    <span className="text-shade-blue italic cursor-pointer ml-2" onClick={onObieClick}>
                      Insurance Quote
                    </span>
                  )}
                </p>
                <div className="grid gap-4 xl:grid-cols-3 md:grid-cols-2 grid-cols-1 mb-3">
                  {inputGroup.map((key: string) => {
                    if (!isConfig && key === 'borrowerRequired') return
                    let input = inputs[key] as any
                    input.history = true
                    return (
                      <div className={`input md:col-span-${input.span || 1}`} key={key}>
                        <RenderInput input={input} Key={key} onChange={onChange} showHistory={showHistory} />
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </Modal>
      {isShowOverlay && (
        <>
          <div
            className="w-100 justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none"
            style={{ zIndex: 70 }}
            onClick={(e) => !e.defaultPrevented && setShowOverlay(false)}
          >
            <div className="bg-white px-10 py-4 rounded shadow-lg" onClick={(e) => e.preventDefault()}>
              <Button link onClick={onSelectFromRolodex}>
                Select from Rolodex...
              </Button>
              {hasPermission('Template') && !!categories[item.Type] && (
                <Button link onClick={onAddToRolodex}>
                  <span>
                    Add this to Rolodex as <b>{item.Type || ''}</b>
                  </span>
                </Button>
              )}
            </div>
            {loading && <LayoutLoading show />}
          </div>
          <div className="opacity-25 fixed inset-0 bg-black" style={{ zIndex: 65 }}></div>
        </>
      )}
      {isShowRolodexModal && <RolodexModal type={item.Type} onClose={onCloseRolodexModal} />}
    </>
  )
}
